import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Heading, Button, Tooltip } from "@foris/avocado-suite";
import { isBlockingScheduledBlocksAssignment } from "@models/Incidences/scheduledBlocksAssignment";
import {
  TOTAL_EXPECTED_BLOCKS_BY_LINK_TYPENAME,
  isTotalExpectedBlocksDeficit,
} from "@models/Incidences/totalExpectedBlocksByLink";
import { AppContext } from "../../context/EditSessionsContext";
import { Types } from "../../context/switchPage.reducer";
import { Types as TypeResult } from "../../context/result.reducer";
import { IContextApp, ContextApp } from "@config/Context/contextApp";
import { Types as FormTypes } from "../../context/formData.reducer";
import { useProgrammingEditStore, clearProgrammingResults } from "@context/useProgrammingEditStore";
import { useProposalMode } from "@services/collections/assignment-proposal/assignmentProposalServices";
import { proposalDecisionKind, type ProposalDecisionKind } from "@utils/assignmentProposal";
import { useActiveProposal } from "../../hooks/useActiveProposal";
import ProposalRejectButton from "../proposal-reject-button";
import css from "./resultsEditHeader.module.scss";

const CONFIRM_PROPOSAL_LABEL: Record<ProposalDecisionKind, string> = {
  apply: "assignation-edit.proposal.actions.apply",
  "pre-approve": "assignation-edit.proposal.actions.approve",
  resend: "assignation-edit.proposal.actions.resend",
  send: "assignation-edit.proposal.actions.send",
};

interface ResultsEditHeaderProps {
  onSubmit: () => void;
  onBack?: () => void;
  canConfirm?: boolean;
}

const ResultsEditHeader: React.FC<ResultsEditHeaderProps> = (props: ResultsEditHeaderProps) => {
  const { onSubmit, onBack, canConfirm } = props;
  const { state, dispatch } = useContext(AppContext);
  const context: IContextApp = useContext(ContextApp);
  const userCanEdit =
    context?.user?.permissions?.update && context?.user?.abilities?.can_edit_assignment;

  const { t } = useTranslation();

  // Programming mode
  const isProgrammingMode = useProgrammingEditStore(s => s.isProgrammingResultsActive);

  // The header is shared with the programming and the sessions-view results; the proposal flow
  // only exists in the traditional assignation results (no programming mode, no onBack override).
  const { isProposalMode, isResolving } = useProposalMode();
  const isTraditionalAssignation = !isProgrammingMode && !onBack;
  const activeProposal = useActiveProposal();
  const isConfirmingProposal = isProposalMode && isTraditionalAssignation;
  const isConfirmBlockedByUnknownMode = isResolving && isTraditionalAssignation;
  const previewSections = useProgrammingEditStore(s => s.previewSections);

  const numberOfSessionsToValidate = useMemo(() => {
    if (isProgrammingMode) {
      return (previewSections ?? []).reduce((sum, section) => sum + section.sessions.length, 0);
    }
    return (
      (state?.form?.savedSessions?.length ?? 0) +
      (state?.form?.sessionsToCreate.length ?? 0) +
      Object.keys(state?.form?.sessionsToDelete ?? {}).length
    );
  }, [
    isProgrammingMode,
    previewSections,
    state?.form?.savedSessions,
    state?.form?.sessionsToCreate,
    state?.form?.sessionsToDelete,
  ]);

  const createValidation = state?.result?.createValidation;
  const editValidation = state?.result?.resultValidation;
  const createUserCanSkipValidations = createValidation
    ? createValidation?.userCanSkipValidations
    : true;
  const editUserCanSkipValidations = editValidation ? editValidation?.userCanSkipValidations : true;
  const userCanSkipValidations = createUserCanSkipValidations && editUserCanSkipValidations;

  // Deficits are informational (`allowed: true`). Only an explicitly disallowed
  // scheduled-block incidence should explain a disabled confirm button.
  const hasBlockingScheduledBlocksError = [
    ...(editValidation?.sessionsPayload ?? []),
    ...(createValidation?.sessionsPayload ?? []),
  ].some(({ validationErrors }) =>
    (validationErrors ?? []).some(isBlockingScheduledBlocksAssignment),
  );

  // CHS-2947 / CHS-2931: unlike the case above, the excess error reaches the frontend
  // even for users holding `can_skip_total_blocks` (the backend keeps `allowed: false`
  // and commits anyway), so its presence alone does NOT mean the save is blocked.
  // `userCanSkipValidations` is what decides; the error only selects the tooltip.
  const hasTotalExpectedBlocksExcess = [
    ...(editValidation?.sessionsPayload ?? []),
    ...(createValidation?.sessionsPayload ?? []),
  ].some(({ validationErrors }) =>
    (validationErrors ?? []).some(
      error =>
        error?.__typename === TOTAL_EXPECTED_BLOCKS_BY_LINK_TYPENAME &&
        !isTotalExpectedBlocksDeficit(error),
    ),
  );

  // The scheduled-blocks reason keeps precedence: it is the older, narrower rule and
  // its presence already implies a blocked save on its own.
  const blockedReasonLabel = hasBlockingScheduledBlocksError
    ? t("assignation-edit.incidence-table.header.scheduled-blocks-blocked")
    : t("assignation-edit.incidence-table.header.total-expected-blocks-blocked");

  const isBlockedReasonVisible =
    hasBlockingScheduledBlocksError || (hasTotalExpectedBlocksExcess && !userCanSkipValidations);

  const title = useMemo(() => {
    if (numberOfSessionsToValidate === 1) {
      return t("assignation-edit.incidence-table.header.single-change-to-validate");
    }
    return t("assignation-edit.incidence-table.header.changes-to-validate", {
      count: numberOfSessionsToValidate,
    });
  }, [numberOfSessionsToValidate]);

  const userCanMutateSessions = () => {
    const abilitiesValidations = {
      InstructorAssignmentNotAllowed: true,
      BlocksAssignmentNotAllowed: true,
      ClassroomsAssignmentNotAllowed: true,
      IntervalsAssignmentNotAllowed: true,
    };

    const sessionPayload = [
      ...(editValidation?.sessionsPayload ?? []),
      ...(createValidation?.sessionsPayload ?? []),
    ];

    if (sessionPayload.length === 0) return true;

    return sessionPayload.every(({ validationErrors }) =>
      validationErrors.every(
        ({ __typename }) => !(__typename && __typename in abilitiesValidations),
      ),
    );
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (isProgrammingMode) {
      clearProgrammingResults();
      dispatch({ type: TypeResult.ResultValidation, payload: null });
      dispatch({ type: TypeResult.CreateValidation, payload: null });
      return;
    }
    dispatch({ type: FormTypes.CleanFormCreateSession });
    dispatch({ type: Types.FormPage, payload: true });
    dispatch({ type: TypeResult.SelectSession, payload: null });
    dispatch({ type: TypeResult.ResultValidation, payload: null });
    dispatch({ type: TypeResult.CreateValidation, payload: null });
  };

  return (
    <header className={css.resultsEditHeader}>
      <Heading className={css.resultsEditHeader_title} type="h3">
        {title}
      </Heading>

      <div className={css.resultsEditHeader_actions}>
        <Button variant="ghost" onClick={handleBack}>
          {t("assignation-edit.incidence-table.header.back-to-edit")}
        </Button>

        {isTraditionalAssignation && <ProposalRejectButton />}

        {userCanEdit && (
          <Tooltip
            label={blockedReasonLabel}
            visible={isBlockedReasonVisible ? undefined : false}
            placement="left"
          >
            <Button
              disabled={
                canConfirm === false ||
                !userCanSkipValidations ||
                !userCanMutateSessions() ||
                isConfirmBlockedByUnknownMode
              }
              onClick={onSubmit}
            >
              {isConfirmingProposal
                ? t(CONFIRM_PROPOSAL_LABEL[proposalDecisionKind(activeProposal)])
                : t("assignation-edit.incidence-table.header.confirm-changes")}
            </Button>
          </Tooltip>
        )}
      </div>
    </header>
  );
};

export default ResultsEditHeader;
